import { useEffect, useState } from "react"
import { UnreadReplyCountCacheService } from "src/services/cache/UnreadReplyCountCacheService"
import { useAsyncSelector } from "./useAsyncSelector"
import { unreadThreadCountSelectorFamily } from "src/recoil/selectors/contact/unreadThreadCountSelectorFamily"

/**
 * 未読返信数を取得するカスタムフック
 * キャッシュを優先的に使用し、キャッシュがない場合のみAPIを呼び出す
 *
 * @param contactId 連絡ID
 * @param skipApi trueの場合、APIを呼び出さずキャッシュのみを使用
 * @returns 未読返信数（キャッシュまたはAPI）
 */
export const useUnreadReplyCount = (contactId: string | undefined, skipApi = false): number | null => {
  const [cachedCount, setCachedCount] = useState<number | null>(null)
  const [shouldFetchFromApi, setShouldFetchFromApi] = useState(false)

  // まずキャッシュをチェック
  useEffect(() => {
    if (!contactId) {
      setCachedCount(null)
      setShouldFetchFromApi(false)
      return
    }

    const cached = UnreadReplyCountCacheService.get(contactId)

    if (cached !== null) {
      // キャッシュヒット
      setCachedCount(cached)
      setShouldFetchFromApi(false)
    } else {
      // キャッシュミス - APIから取得する必要がある
      setCachedCount(null)
      setShouldFetchFromApi(!skipApi)
    }
  }, [contactId, skipApi])

  // キャッシュがない場合のみAPIを呼び出す
  const { value: apiCount } = useAsyncSelector(
    unreadThreadCountSelectorFamily({
      contactIds: shouldFetchFromApi && contactId ? contactId : undefined,
    })
  )

  // APIから取得した値をキャッシュに保存
  useEffect(() => {
    if (apiCount !== null && apiCount !== undefined && contactId && shouldFetchFromApi) {
      UnreadReplyCountCacheService.set(contactId, apiCount)
      setCachedCount(apiCount)
      setShouldFetchFromApi(false)
    }
  }, [apiCount, contactId, shouldFetchFromApi])

  // キャッシュがあればキャッシュを、なければAPIの結果を返す
  return cachedCount !== null ? cachedCount : apiCount ?? null
}
